import React, { useEffect, useState } from "react";
import { useApp } from "@/contexts/AppContext";
import { api, formatError } from "@/lib/api";
import { PageHeader, StatusBadge } from "@/components/common/Bits";
import { Sparkles, Play, Clock, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { runAIPrompt } from "@/lib/ai";
import { useAIOutputs } from "@/hooks/useAIOutputs";
import ConfirmRegenerateDialog from "@/components/ai/ConfirmRegenerateDialog";

const CATEGORIES = [
  { key: "case_summary", icon: "📋", titleEl: "Σύνοψη Υπόθεσης", desc: "Δομημένη ανάλυση: κατηγορίες, διάδικοι, χρονολόγιο, βασικά αποδεικτικά, κίνδυνοι, ευκαιρίες υπεράσπισης." },
  { key: "timeline", icon: "🕒", titleEl: "Χρονολόγιο Γεγονότων", desc: "Χρονολογικό αρχείο με ημερομηνία, συμβάν, εμπλεκόμενους, πηγή, σημαντικότητα και νομική κρισιμότητα." },
  { key: "witness_contradictions", icon: "⚖️", titleEl: "Αντιφάσεις Μαρτύρων", desc: "Εντοπισμός αντιφάσεων σε καταθέσεις με πηγές και βαρύτητα για αντεξέταση." },
  { key: "cross_examination", icon: "🎯", titleEl: "Αντεξέταση Μάρτυρα", desc: "Trial-lawyer leading questions εστιασμένες σε αντιφάσεις, μνήμη, κίνητρο, αξιοπιστία." },
  { key: "prosecution_weaknesses", icon: "🔍", titleEl: "Αδυναμίες Κατηγορούσας Αρχής", desc: "Ελλιπή αποδεικτικά, αναξιόπιστοι μάρτυρες, δικονομικά ζητήματα, εναλλακτικές εξηγήσεις." },
  { key: "defense_theory", icon: "🛡️", titleEl: "Υπερασπιστική Γραμμή", desc: "Πλήρης trial strategy: κεντρικά θέματα, υποστηρικτικά γεγονότα, αδυναμίες, στρατηγική ακροαματικής." },
  { key: "devils_advocate", icon: "😈", titleEl: "Devil's Advocate — Οπτική Κατηγορίας", desc: "Το AI παίζει τον εισαγγελέα: επιτίθεται στη γραμμή σου, εντοπίζει ευπάθειες ανά σοβαρότητα και προτείνει αντίμετρα." },
  { key: "expert_analysis", icon: "🔬", titleEl: "Ανάλυση Πραγματογνωμοσύνης", desc: "Critical analysis εκθέσεων ειδικών: συμπεράσματα, υποθέσεις, αδυναμίες, μεθοδολογία." },
  { key: "trial_preparation", icon: "📚", titleEl: "Προετοιμασία Δίκης", desc: "Πλήρες υπόμνημα προετοιμασίας — γεγονότα, κατηγορίες, μάρτυρες, αποδεικτικά, στρατηγική." },
  { key: "appeal_grounds", icon: "📜", titleEl: "Λόγοι Έφεσης", desc: "Δικονομικά σφάλματα, αποδεικτικά ζητήματα, κενά αιτιολογίας, παραβιάσεις δικαιωμάτων." },
  { key: "hearing_prep", icon: "🏛️", titleEl: "Προετοιμασία Δικασίμου", desc: "Υπόμνημα δικασίμου με γεγονότα, νομικά σημεία, έγγραφα και προφορικές παραστάσεις." },
];

export default function AIPrompts() {
  const { t, currentCase } = useApp();
  const [running, setRunning] = useState(null);
  const [model, setModel] = useState("claude");
  const [custom, setCustom] = useState("");
  const [witnesses, setWitnesses] = useState([]);
  const [witness, setWitness] = useState("");
  const { outputs: results, addOutput } = useAIOutputs(currentCase?.id);
  const [confirmKey, setConfirmKey] = useState(null);

  useEffect(() => {
    if (!currentCase) return;
    api.get(`/cases/${currentCase.id}/witnesses`).then(r => setWitnesses(r.data)).catch(()=>{});
  }, [currentCase]);

  const run = async (key) => {
    setRunning(key);
    try {
      const payload = { prompt_key: key, case_id: currentCase.id, model, custom_instructions: custom };
      const witnessId = key === "cross_examination" ? witness : null;
      if (witnessId) payload.witness_id = witnessId;
      const result = await runAIPrompt(payload);
      addOutput({
        id: result.output_id, prompt_key: key, title: result.title, model: result.model,
        confidence: result.confidence, output: result.output,
        created_at: result.completed_at, witness_id: witnessId || null,
      });
      toast.success(`AI ✓ ${result.title}`);
    } catch (e) { toast.error(e.message || "Σφάλμα AI"); }
    finally { setRunning(null); }
  };

  const handleRunClick = (key) => {
    const witnessId = key === "cross_examination" ? witness : null;
    const exists = results.some(o => o.prompt_key === key && (!witnessId || o.witness_id === witnessId));
    if (exists) setConfirmKey(key);
    else run(key);
  };

  const confirmTarget = results.find(o => o.prompt_key === confirmKey);

  return (
    <div data-testid="ai-prompts-page">
      <PageHeader
        title="Εντολές AI"
        subtitle="Έτοιμη βιβλιοθήκη AI prompts για ποινικολόγους. Όλες οι αναλύσεις είναι context-aware βάσει της υπόθεσης."
      />

      <div className="surface-card p-5 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-mono">{t("select_model")}</label>
            <select
              data-testid="ai-model-select"
              value={model} onChange={e => setModel(e.target.value)}
              className="mt-1.5 w-full bg-muted/30 border border-border/80 rounded-md px-3 py-2 text-[13px]">
              <option value="claude">Claude (Sonnet 5 / Opus 4.8 — νομική ανάλυση)</option>
              <option value="gpt">OpenAI GPT-5 (στρατηγική &amp; report)</option>
            </select>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-mono">Εστίαση σε μάρτυρα (για αντεξέταση)</label>
            <select
              data-testid="ai-witness-select"
              value={witness} onChange={e => setWitness(e.target.value)}
              className="mt-1.5 w-full bg-muted/30 border border-border/80 rounded-md px-3 py-2 text-[13px]">
              <option value="">—</option>
              {witnesses.map(w => <option key={w.id} value={w.id}>{w.full_name}</option>)}
            </select>
          </div>
          <div className="md:col-span-1">
            <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-mono">{t("custom_instructions")}</label>
            <input
              data-testid="ai-custom-input"
              value={custom} onChange={e => setCustom(e.target.value)}
              placeholder="προαιρετικές οδηγίες..."
              className="mt-1.5 w-full bg-muted/30 border border-border/80 rounded-md px-3 py-2 text-[13px]"/>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {CATEGORIES.map(c => (
          <div key={c.key} className="surface-card surface-card-hover p-5 tracing-beam relative" data-testid={`prompt-card-${c.key}`}>
            <div className="text-2xl mb-2 select-none">{c.icon}</div>
            <h3 className="font-heading font-semibold text-[14px] mb-1.5">{c.titleEl}</h3>
            <p className="text-[12.5px] text-muted-foreground leading-relaxed mb-4 font-mono">{c.desc}</p>
            <button
              data-testid={`prompt-run-${c.key}`}
              onClick={() => handleRunClick(c.key)} disabled={running === c.key}
              className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-md bg-[hsl(var(--gold)/0.12)] gold-text gold-border border text-[12.5px] font-medium hover:bg-[hsl(var(--gold)/0.2)] transition-colors disabled:opacity-60"
            >
              {running === c.key ? <><Sparkles className="size-3.5 animate-pulse-soft"/>AI thinking…</> : <><Play className="size-3.5"/>{t("run_prompt")}</>}
            </button>
          </div>
        ))}
      </div>

      {results.length > 0 && (
        <div className="mt-8">
          <h2 className="font-heading font-semibold text-[15px] mb-3 flex items-center gap-2">
            <Clock className="size-4 gold-text"/>Πρόσφατες AI έξοδοι
          </h2>
          <div className="space-y-2">
            {results.slice(0, 5).map(r => (
              <a key={r.id} href="/reports" className="flex items-center justify-between gap-3 surface-card surface-card-hover p-3.5" data-testid={`ai-result-${r.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] font-medium truncate">{r.title}</div>
                  <div className="text-[11px] text-muted-foreground font-mono">{r.model} · {new Date(r.created_at).toLocaleString("el-GR")}</div>
                </div>
                {r.confidence != null && <StatusBadge tone="gold">{r.confidence}%</StatusBadge>}
                <ChevronRight className="size-4 text-muted-foreground"/>
              </a>
            ))}
          </div>
        </div>
      )}

      <ConfirmRegenerateDialog
        open={!!confirmKey} onOpenChange={(v) => !v && setConfirmKey(null)}
        existingDate={confirmTarget?.created_at}
        onConfirm={() => { const k = confirmKey; setConfirmKey(null); run(k); }}
      />
    </div>
  );
}
